import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useShopGroupsContext } from '../../../../contexts/ShopGroupContext';
import ShopGroup from '../../../../entities/shop-group.entity';
import { IconTitle } from '../../../../components';
import { clone } from '../../../../utils/helpers';
import useStyles from './styles';
import Form from './components/Form';
import { RecursiveKeyOf } from '../../../../utils/types';
import { set } from '../../../../utils/objects';
import useShopGroups from '../../../../hooks/data/useShopGroups';
import { useStorage } from '../../../../hooks';

export const BasicData = (): JSX.Element => {
  const styles = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const history = useNavigate();
  const { shopGroup: loadedShopGroup, loading, handleSelectShopGroup } = useShopGroupsContext();
  const [shopGroup, setShopGroup] = useState<ShopGroup>(loadedShopGroup || ({} as ShopGroup));
  const [fileToUpload, setFileToUpload] = useState<File | null>();
  const shopGroupRepository = useShopGroups();

  useEffect(() => {
    setShopGroup(clone(loadedShopGroup || ({} as ShopGroup)));
  }, [loadedShopGroup]);

  const handleFieldChange = (key: RecursiveKeyOf<ShopGroup>, value: string | null | number | boolean) => {
    const newShopGroup = set(shopGroup, key, value);
    setShopGroup(newShopGroup);
  };

  const handleUpload = (file: File | null) => {
    setFileToUpload(file);
  };

  const storage = useStorage();

  const handleFetchShopGroupAvatar = async (id: string): Promise<string | null | undefined> => {
    if (fileToUpload === undefined) {
      return shopGroup.imageUrl;
    }

    if (!fileToUpload) {
      return null;
    }

    const extension = fileToUpload.name.split('.').pop();
    const avatarUrl = await storage.upload(`/shop-groups/${id}.${extension}`, fileToUpload);
    handleFieldChange('imageUrl', avatarUrl);
    return avatarUrl;
  };

  const handleCreateShopGroup = async () => {
    const image = await handleFetchShopGroupAvatar(shopGroup.id);
    const { id } = await shopGroupRepository.create({ ...shopGroup, imageUrl: image });
    history(`/shop-groups/${id}`);
  };

  const updateShopGroup = async () => {
    await shopGroupRepository.update({
      ...shopGroup,
      imageUrl: await handleFetchShopGroupAvatar(shopGroup.id),
    });

    handleSelectShopGroup(shopGroup);
  };

  const handleSubmit = async () => {
    try {
      if (shopGroup.id) {
        await updateShopGroup();
      } else {
        await handleCreateShopGroup();
      }

      enqueueSnackbar('Grupo de loja salvo com sucesso!', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Não foi possível salvar, tente novamente mais tarde', { variant: 'error' });
    }
  };

  if (loading) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <IconTitle title="Dados Básicos" />
      <Form
        shopGroup={shopGroup}
        onSubmit={handleSubmit}
        handleUpload={handleUpload}
        handleFieldChange={handleFieldChange}
        fileToUpload={fileToUpload}
      />
    </div>
  );
};

export default BasicData;
